/**
 * KbReview — чтение базы знаний сделки перед одобрением (Ворота 2).
 *
 * До 02.08 в кабинете были только кнопки «Собрать БЗ» и «Одобрить БЗ»: текст
 * топиков посмотреть было негде, и оператор жал «Одобрить» вслепую, хотя именно
 * из этих топиков бот клиента берёт ВСЕ факты — цены, сроки, гарантии.
 *
 * 06.08: system_prompt здесь НЕ показывается — он живёт на вкладке «Инструкция»
 * (HIDDEN_IN_KB). Один и тот же текст в двух местах разъезжается.
 *
 * Показывает: сколько топиков заполнено, каждый топик раскрывается с полным
 * текстом, статус версии (черновик/одобрен) и кем версия написана — воркером
 * `kb_build` или руками оператора (правки человека пересборка не перезатирает).
 */

import { useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, ChevronDown, ChevronUp, Loader2, PencilLine, Bot, Pencil, Paperclip, Plus, Trash2, X, Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { kbApi, type KbTopicItem } from '@/api/client'
import { KbFiles } from '@/components/KbFiles'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

/** Рекомендуемые разделы: порядок и человеческие названия. Обязателен из них
 *  только system_prompt (05.08) — остальное фактура, её объём произвольный. */
const TOPIC_TITLES: Record<string, string> = {
  about: 'О компании',
  product: 'Продукты и услуги',
  pricing: 'Цены',
  process: 'Процесс и каналы',
  faq: 'Частые вопросы',
  handoff_rules: 'Правила передачи оператору',
  system_prompt: 'Персона (системный промпт)',
}
// Словарь человеческих имён для разделов, доставшихся от прежней авто-сборки.
// Новые разделы приходят со своим title — словарь для них не нужен.
const LEGACY_NAMES = Object.keys(TOPIC_TITLES)

/**
 * Системный промпт живёт на вкладке «Инструкция» — он задаёт поведение агента и
 * это главное поле карточки. В базе знаний его не показываем: иначе один и тот
 * же текст правится в двух местах и легко разъезжается.
 */
const HIDDEN_IN_KB = ['system_prompt']


/** Человеческое имя топика: у своих топиков его нет в словаре — берём title или slug. */
function titleOf(name: string, title?: string | null): string {
  return title || TOPIC_TITLES[name] || name
}

const MOCK_TOPICS: KbTopicItem[] = LEGACY_NAMES.map((topic, i) => ({
  id: `mock-${topic}`,
  topic,
  title: TOPIC_TITLES[topic],
  current_version_id: `v-${topic}`,
  current_status: i < 2 ? 'approved' : 'draft',
  version: 1,
  content_md: `# ${TOPIC_TITLES[topic]}\n\nДемо-текст топика «${topic}» для разработки.`,
  authored_by_operator: i === 0,
  updated_at: null,
}))

/** Строка markdown-таблицы → ячейки (без крайних разделителей). */
function tableCells(line: string): string[] {
  return line.replace(/^\||\|$/g, '').split('|').map((c) => c.trim())
}

const TABLE_DIVIDER_RE = /^\|?[\s:-]*-[\s:|-]*\|?$/

/**
 * Мини-разметка markdown без библиотеки: заголовки жирным, таблицы — таблицами,
 * остальное — как есть. Таблица нужна ради прайса: именно там оператор сверяет
 * цифры, а «палки» столбцов читать невозможно. Тянуть markdown-парсер в бандл
 * ради двух правил не стоит.
 */
function renderMarkdown(md: string) {
  const lines = md.split('\n')
  const out: ReactNode[] = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Таблица: подряд идущие строки, начинающиеся с «|»
    if (line.trim().startsWith('|')) {
      const block: string[] = []
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        block.push(lines[i])
        i++
      }
      i--
      const rows = block.filter((r) => !TABLE_DIVIDER_RE.test(r.trim()))
      if (rows.length) {
        const [head, ...body] = rows
        out.push(
          <div key={`t-${i}`} className="my-2 overflow-x-auto">
            <table className="w-full text-[12px] border-collapse">
              <thead>
                <tr>
                  {tableCells(head).map((c, ci) => (
                    <th
                      key={ci}
                      className="border-b border-slate-200 px-2 py-1 text-left font-medium text-slate-500 whitespace-nowrap"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((r, ri) => (
                  <tr key={ri}>
                    {tableCells(r).map((c, ci) => (
                      <td
                        key={ci}
                        className="border-b border-slate-100 px-2 py-1 text-slate-700 whitespace-nowrap"
                      >
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
      continue
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      out.push(
        <p
          key={i}
          className={
            heading[1].length === 1
              ? 'font-semibold text-slate-900 mt-3 first:mt-0'
              : 'font-medium text-slate-800 mt-3'
          }
        >
          {heading[2]}
        </p>
      )
      continue
    }

    if (!line.trim()) {
      out.push(<div key={i} className="h-2" />)
      continue
    }

    out.push(
      <p key={i} className="text-slate-700">
        {line}
      </p>
    )
  }

  return out
}

interface KbReviewProps {
  dealId: string
  /** Сообщить кокпиту, что правка закрыла Ворота 2 (нужно одобрить заново). */
  onEdited?: () => void
}

export function KbReview({ dealId, onEdited }: KbReviewProps) {
  const [openTopic, setOpenTopic] = useState<string | null>(null)
  const [editingTopic, setEditingTopic] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [uploadingTopic, setUploadingTopic] = useState<string | null>(null)
  const [uploadNote, setUploadNote] = useState<{ topic: string; text: string; warn: boolean } | null>(null)
  const [deletingTopic, setDeletingTopic] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  // Подвкладки базы знаний: разделы с текстом, документы и таблицы-первоисточники.
  const [pane, setPane] = useState<'topics' | 'documents' | 'tables'>('topics')
  const [renaming, setRenaming] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  // Карта топиков нужна в saveEdit, объявленном выше места, где она считается.
  const byTopicRef = useRef(new Map<string, KbTopicItem>())
  const qc = useQueryClient()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['kb', dealId],
    queryFn: async (): Promise<KbTopicItem[]> => {
      if (USE_MOCK) return MOCK_TOPICS
      return kbApi.list(dealId)
    },
    staleTime: 30 * 1000,
  })

  function startEdit(topic: KbTopicItem | undefined, name: string) {
    setSaveError(null)
    setEditingTopic(name)
    setOpenTopic(name)
    setDraft(topic?.content_md ?? `# ${titleOf(name, topic?.title)}\n\n`)
  }

  function cancelEdit() {
    setEditingTopic(null)
    setDraft('')
    setSaveError(null)
  }

  /**
   * Файл → текст топика. Бот не «открывает» вложения: он отвечает по тексту базы
   * знаний, поэтому сервер разбирает файл и вставляет содержимое в топик.
   */
  async function uploadFile(name: string, file: File) {
    setUploadingTopic(name)
    setUploadNote(null)
    try {
      if (USE_MOCK) {
        setUploadNote({ topic: name, text: `Демо-режим: файл «${file.name}» не загружается.`, warn: true })
        return
      }
      const res = await kbApi.uploadFile(dealId, name, file)
      await qc.invalidateQueries({ queryKey: ['kb', dealId] })
      setOpenTopic(name)
      const parts = [`Файл «${res.filename}» добавлен в топик: ${res.extracted_chars.toLocaleString('ru-RU')} знаков текста.`]
      if (res.truncated) parts.push('Текст обрезан по лимиту — проверьте, что осталось главное.')
      if (res.note) parts.push(res.note)
      setUploadNote({ topic: name, text: parts.join(' '), warn: res.truncated || !!res.note })
      onEdited?.()
    } catch (e) {
      setUploadNote({
        topic: name,
        text: e instanceof Error ? e.message : 'Не удалось загрузить файл.',
        warn: true,
      })
    } finally {
      setUploadingTopic(null)
    }
  }

  async function createTopic(title: string) {
    setCreateError(null)
    try {
      if (USE_MOCK) {
        setCreateError('Демо-режим: раздел не создаётся.')
        return
      }
      const res = await kbApi.createTopic(dealId, title)
      await qc.invalidateQueries({ queryKey: ['kb', dealId] })
      setCreating(false)
      setNewName('')
      // Сразу открываем редактор нового раздела — иначе он останется пустым.
      startEdit(undefined, res.topic)
      onEdited?.()
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Не удалось создать раздел.')
    }
  }

  async function renameTopic(name: string) {
    const title = renameDraft.trim()
    if (!title) return
    try {
      if (!USE_MOCK) await kbApi.rename(dealId, name, title)
      await qc.invalidateQueries({ queryKey: ['kb', dealId] })
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Не удалось переименовать.')
    } finally {
      setRenaming(null)
      setRenameDraft('')
    }
  }

  async function removeTopic(name: string) {
    setDeletingTopic(name)
    setSaveError(null)
    try {
      if (!USE_MOCK) await kbApi.remove(dealId, name)
      await qc.invalidateQueries({ queryKey: ['kb', dealId] })
      if (openTopic === name) setOpenTopic(null)
      if (editingTopic === name) cancelEdit()
      onEdited?.()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Не удалось удалить топик.')
    } finally {
      setDeletingTopic(null)
      setConfirmDelete(null)
    }
  }

  async function saveEdit(name: string) {
    setSaving(true)
    setSaveError(null)
    try {
      if (!USE_MOCK) {
        await kbApi.save(dealId, name, draft, titleOf(name, byTopicRef.current.get(name)?.title))
      }
      await qc.invalidateQueries({ queryKey: ['kb', dealId] })
      setEditingTopic(null)
      setDraft('')
      onEdited?.()
    } catch {
      setSaveError('Не удалось сохранить. Проверьте связь и попробуйте ещё раз.')
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загружаю базу знаний…
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        Не удалось загрузить базу знаний. Обновите страницу.
      </div>
    )
  }

  const topics = data ?? []
  const byTopic = new Map(topics.map((t) => [t.topic, t]))
  byTopicRef.current = byTopic
  // 10.08: показываем ТОЛЬКО существующие разделы, по алфавиту.
  //
  // Раньше шесть шаблонных разделов («О компании», «Цены», «Процесс»…) были
  // вшиты в список и рисовались всегда, даже когда их не было в данных. У такой
  // строки нет ни содержимого, ни корзины — удалить её было нечем, а выглядела
  // она как настоящий раздел. Обязательного набора больше нет: что оператор
  // завёл, то и видно, и у каждого есть корзина.
  const allNames = topics
    .map((t) => t.topic)
    .filter((n) => !HIDDEN_IN_KB.includes(n))
    .sort()
  const approved = topics.filter((t) => t.current_status === 'approved').length
  const filled = allNames.filter((name) => byTopic.get(name)?.content_md).length

  return (
    <div className="rounded-md border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-slate-50">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">База знаний</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <div className="flex gap-0.5 rounded border p-0.5">
            {([
              ['topics', 'Разделы'],
              ['documents', 'Документы'],
              ['tables', 'Таблицы'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPane(key)}
                className={`rounded px-2 py-0.5 ${pane === key ? 'bg-accent font-medium text-slate-800' : ''}`}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="text-slate-300">·</span>
          <span>заполнено {filled} из {allNames.length}</span>
          <span className="text-slate-300">·</span>
          <span>одобрено {approved}</span>
        </div>
      </div>

      {pane !== 'topics' ? (
        <div className="bg-white p-4">
          <KbFiles dealId={dealId} kind={pane === 'tables' ? 'table' : 'document'} />
        </div>
      ) : (
      <>
      <p className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] leading-relaxed text-slate-500">
        Системный промпт правится на вкладке «Инструкция» — здесь только фактура.
        Скрепка — прикрепить прайс или документ (xlsx, docx, pdf, csv, txt).
        Текст из файла попадёт прямо в топик: бот отвечает только по тексту базы
        знаний, поэтому файл разбирается при загрузке. Повторная загрузка того же
        файла заменяет прежний кусок.
      </p>

      <div className="divide-y divide-slate-100 bg-white">
        {allNames.length === 0 && (
          <p className="px-4 py-8 text-center text-xs text-muted-foreground">
            Разделов пока нет. Добавьте те, что нужны именно этому бизнесу, —
            прайс, гарантия, регламент монтажа. Или подключите таблицу и
            документ: их текст ляжет разделом сам.
          </p>
        )}
        {allNames.map((name) => {
          const t = byTopic.get(name)
          const isEditing = editingTopic === name
          const isOpen = openTopic === name || isEditing
          const empty = !t?.content_md

          return (
            <div key={name}>
              <div className="w-full flex items-center justify-between gap-3 pr-3">
                <button
                  type="button"
                  className="flex-1 flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-sm"
                  onClick={() => {
                    if (isEditing) return
                    if (empty) {
                      startEdit(t, name)
                      return
                    }
                    setOpenTopic(isOpen ? null : name)
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-slate-700 truncate">
                      {titleOf(name, t?.title)}
                    </span>
                    {t?.authored_by_operator ? (
                      <PencilLine className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-label="правил оператор" />
                    ) : (
                      !empty && <Bot className="h-3.5 w-3.5 shrink-0 text-slate-300" aria-label="собрано автоматически" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {empty ? (
                      <Badge variant="gate_locked">пусто</Badge>
                    ) : t?.current_status === 'approved' ? (
                      <Badge variant="gate_passed">одобрен</Badge>
                    ) : (
                      <Badge variant="urgency_yellow">черновик</Badge>
                    )}
                    {!empty &&
                      (isOpen ? (
                        <ChevronUp className="h-4 w-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-slate-400" />
                      ))}
                  </div>
                </button>
                {!isEditing && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <label
                      className="cursor-pointer rounded p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                      title="Прикрепить файл (xlsx, docx, pdf, csv, txt) — его текст добавится в топик"
                    >
                      {uploadingTopic === name ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Paperclip className="h-3.5 w-3.5" />
                      )}
                      <input
                        type="file"
                        className="sr-only"
                        accept=".xlsx,.xlsm,.docx,.pdf,.csv,.txt,.md"
                        disabled={uploadingTopic !== null}
                        onChange={(e) => {
                          const f = e.target.files?.[0]
                          e.target.value = '' // тот же файл можно выбрать повторно
                          if (f) uploadFile(name, f)
                        }}
                      />
                    </label>
                    {t && (
                      <button
                        type="button"
                        className="rounded p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        title="Переименовать раздел"
                        aria-label={`Переименовать: ${titleOf(name, t?.title)}`}
                        onClick={() => {
                          setRenaming(name)
                          setRenameDraft(titleOf(name, t?.title))
                        }}
                      >
                        <PencilLine className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="rounded p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                      title={empty ? 'Заполнить текст' : 'Править текст'}
                      aria-label={empty ? `Заполнить: ${titleOf(name, t?.title)}` : `Править: ${titleOf(name, t?.title)}`}
                      onClick={() => startEdit(t, name)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    {/* Системный промпт не удаляется: он единственный обязательный,
                        без него агента нет и Ворота 2 не проходятся. */}
                    {t && name !== 'system_prompt' && (
                      <button
                        type="button"
                        disabled={deletingTopic === name}
                        className="rounded p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Удалить топик"
                        aria-label={`Удалить: ${titleOf(name, t?.title)}`}
                        onClick={() => setConfirmDelete(name)}
                      >
                        {deletingTopic === name ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {renaming === name && (
                <div className="mx-4 mb-2 flex items-center gap-2">
                  <input
                    autoFocus
                    className="flex-1 rounded border px-2 py-1.5 text-sm bg-background"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') renameTopic(name)
                      if (e.key === 'Escape') setRenaming(null)
                    }}
                  />
                  <Button size="sm" className="h-7 text-xs" onClick={() => renameTopic(name)}>
                    Сохранить
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setRenaming(null)}
                  >
                    Отмена
                  </Button>
                </div>
              )}

              {confirmDelete === name && (
                <div className="mx-4 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 space-y-2">
                  <p>
                    Удалить топик «{titleOf(name, t?.title)}» вместе со всеми версиями?
                    Восстановить будет нельзя. Тест-гейт после удаления потребует
                    повторного прогона — состав базы знаний изменится.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100"
                      disabled={deletingTopic === name}
                      onClick={() => removeTopic(name)}
                    >
                      Удалить
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setConfirmDelete(null)}
                    >
                      Отмена
                    </Button>
                  </div>
                </div>
              )}

              {uploadNote?.topic === name && (
                <div
                  className={`mx-4 mb-2 rounded-md border px-3 py-2 text-xs ${
                    uploadNote.warn
                      ? 'border-amber-200 bg-amber-50 text-amber-800'
                      : 'border-green-200 bg-green-50 text-green-800'
                  }`}
                >
                  {uploadNote.text}
                </div>
              )}

              {isEditing && (
                <div className="px-4 pb-4 pt-1 space-y-2">
                  <textarea
                    className="w-full h-72 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-[13px] leading-relaxed font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    spellCheck
                    autoFocus
                  />
                  <p className="text-[11px] text-slate-400">
                    Разметка markdown: <code>#</code> — заголовок, <code>-</code> — список,{' '}
                    <code>|</code> — колонки таблицы. Сохранение создаёт новую версию —
                    черновик за вашим авторством; пересборка БЗ его не перезапишет.
                    {t?.current_status === 'approved' && (
                      <> Топик был одобрен — после правки БЗ нужно одобрить заново.</>
                    )}
                  </p>
                  {saveError && (
                    <p className="text-xs text-red-600">{saveError}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="gap-2 text-xs"
                      disabled={saving || !draft.trim()}
                      onClick={() => saveEdit(name)}
                    >
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      Сохранить
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-2 text-xs"
                      disabled={saving}
                      onClick={cancelEdit}
                    >
                      <X className="h-3 w-3" />
                      Отмена
                    </Button>
                  </div>
                </div>
              )}

              {isOpen && !isEditing && t?.content_md && (
                <div className="px-4 pb-4 pt-1">
                  <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2.5 text-[13px] leading-relaxed max-h-[28rem] overflow-y-auto whitespace-pre-wrap break-words">
                    {renderMarkdown(t.content_md)}
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <p className="text-[11px] text-slate-400">
                      версия {t.version ?? 1} ·{' '}
                      {t.authored_by_operator
                        ? 'правил оператор — пересборка БЗ этот текст не тронет'
                        : 'собрано автоматически из анкеты'}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 text-xs h-7"
                      onClick={() => startEdit(t, name)}
                    >
                      <Pencil className="h-3 w-3" />
                      Править
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {/* Свой топик: обязательная семёрка закрывает типовую фактуру, но у клиента
            бывает своё — регламент монтажа, условия для дилеров, сезонные акции.
            Такие топики не участвуют в Воротах 2, но уезжают боту вместе с БЗ. */}
        <div className="px-4 py-3 bg-slate-50/60">
          {!creating ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs h-7"
              onClick={() => {
                setCreating(true)
                setNewName('')
                setCreateError(null)
              }}
            >
              <Plus className="h-3 w-3" />
              Добавить раздел
            </Button>
          ) : (
            <div className="space-y-2">
              <input
                autoFocus
                className="w-full rounded border px-2 py-1.5 text-sm bg-background"
                placeholder="Например: Гарантия и рекламации"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newName.trim()) createTopic(newName.trim())
                }}
              />
              <p className="text-[11px] text-slate-500">
                Название любое — кириллица, пробелы, знаки. Технический ключ для
                выгрузки боту система выведет сама.
              </p>
              {createError && <p className="text-[11px] text-amber-700">{createError}</p>}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="text-xs h-7"
                  disabled={!newName.trim()}
                  onClick={() => createTopic(newName.trim())}
                >
                  Создать
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7"
                  onClick={() => setCreating(false)}
                >
                  Отмена
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  )
}
